import * as classNames from "classnames";
import { MouseEvent, memo } from "react";
import RefreshIcon from "./arbiter-icon/refresh-ccw-01_filled.svg";
import { Flex } from "./flex";
import CopyIcon from "./icon-old/copy.svg";
import DownArrowIcon from "./icon-old/down-arrow.svg";
import TrashIcon from "./icon-old/trash2.svg";
import UpArrowIcon from "./icon-old/up-arrow.svg";
import * as sectionToolbarCss from "./section-toolbar.module.css";

enum Direction {
  UP = "up",
  DOWN = "down",
}

export const SectionToolbar = <T extends { duplicate?: (value?: T) => T }>({
  open,
  index,
  totalSections,
  sections,
  setSections,
  setSelected,
  setModified,
  canDuplicate = true,
  onRefresh,
  extraSpace = false,
  disableDelete = false,
  optionalSectionDuplicator,
  minimumSections = 1,
}: {
  open: boolean;
  index: number;
  totalSections: number;
  sections: T[];
  setSections: (value: T[]) => void;
  setSelected: (value: string | undefined) => void;
  setModified: (value: boolean) => void;
  disableDelete?: boolean;
  canDuplicate?: boolean;
  onRefresh?: () => void;
  extraSpace?: boolean;
  optionalSectionDuplicator?: (section: T) => T;
  minimumSections?: number;
}) => {
  if (!open) {
    return null;
  }

  const removeSection = (index: number) => {
    const newSections = sections.filter((_, i) => i !== index);
    setSections(newSections);

    setSelected(undefined);
    setModified(true);
    return newSections;
  };

  const duplicateSection = (index: number) => {
    const newSections = [...sections];
    const sectionToDuplicate = newSections[index];
    if (!sectionToDuplicate?.duplicate && !optionalSectionDuplicator) {
      return;
    }
    if (optionalSectionDuplicator) {
      newSections.splice(
        index + 1,
        0,
        optionalSectionDuplicator(sectionToDuplicate),
      );
    } else if (sectionToDuplicate.duplicate) {
      newSections.splice(
        index + 1,
        0,
        sectionToDuplicate.duplicate(sectionToDuplicate),
      );
    }
    setSections(newSections);
    setSelected((index + 1).toString());
    setModified(true);
  };

  const moveSection = (index: number, direction: Direction) => {
    const newSections = [...sections];
    const sectionToMove = newSections[index];
    if (!sectionToMove) {
      return;
    }

    // Special handling for header marker: if we're dealing with a packing slip's header marker
    if ((sectionToMove as any)?.type === "header-marker") {
      // Find the index of the first table
      const tableIndex = newSections.findIndex(
        (section) => (section as any)?.type === "order-items",
      );

      // Don't allow moving a header marker after a table
      if (
        direction === Direction.DOWN &&
        tableIndex !== -1 &&
        index + 1 >= tableIndex
      ) {
        return;
      }
    }

    newSections.splice(index, 1);

    const newIndex = direction === Direction.UP ? index - 1 : index + 1;

    newSections.splice(newIndex, 0, sectionToMove);

    setSections(newSections);
    setSelected(newIndex.toString());
    setModified(true);
  };

  const canDelete = totalSections > minimumSections && !disableDelete;
  const canMoveDown = index < totalSections - 1;
  const canMoveUp = index > 0;
  const editOptions = [];
  if (canDuplicate) {
    editOptions.push({
      helperText: "Duplicate",
      icon: <CopyIcon style={{ width: "20px", height: "20px" }} />,
      onClick: (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        duplicateSection(index);
      },
    });
  }
  if (canDelete) {
    editOptions.push({
      helperText: "Delete",
      icon: <TrashIcon />,
      onClick: (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        removeSection(index);
      },
    });
  }
  if (onRefresh) {
    editOptions.push({
      helperText: "Refresh",
      icon: <RefreshIcon />,
      onClick: onRefresh,
    });
  }
  if (canMoveUp) {
    editOptions.push({
      helperText: "Move up",
      icon: <UpArrowIcon />,
      onClick: (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        moveSection(index, Direction.UP);
      },
    });
  }
  if (canMoveDown) {
    editOptions.push({
      helperText: "Move down",
      icon: <DownArrowIcon />,
      onClick: (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        moveSection(index, Direction.DOWN);
      },
    });
  }
  if (!editOptions.length) {
    return null;
  }

  return (
    <Flex
      className={classNames(
        sectionToolbarCss.sectionToolbar,
        extraSpace && sectionToolbarCss.extraSpace,
      )}
      dir="column"
      gap="none"
    >
      {editOptions.map((option, index) => (
        <Flex
          align="center"
          className={sectionToolbarCss.toolbarOption}
          justify="center"
          key={index}
          onMouseDown={option.onClick}
        >
          {option.icon}
        </Flex>
      ))}
    </Flex>
  );
};

export default memo(SectionToolbar) as typeof SectionToolbar;
